// src/configureWebObservability.ts
import { init as apmInit } from "@elastic/apm-rum";
function configureWebObservability(config) {
  return _configureWebObservability(config);
}
function _configureWebObservability(config) {
  if (typeof window === "undefined") {
    return null;
  }
  if (!config.serviceName) {
    console.error("RUM error: Service name is required");
    return null;
  }
  const { disableInstrumentations, ignoreTransactions, ...restOfConfig } = config;
  const builtConfig = {
    serviceVersion: "",
    environment: "local",
    transactionSampleRate: 0.05,
    sendCredentials: true,
    serverUrl: window.location.origin,
    serverUrlPrefix: "/dGVsZW1ldHJ5",
    ignoreTransactions: [
      /.*yw5hbhl0awnz.*/,
      /.*clarity.*/,
      /.*configcat.*/,
      /.*usetiful.*/,
      ...config.ignoreTransactions ?? []
    ],
    disableInstrumentations: [
      "error",
      "click",
      ...config.disableInstrumentations ?? []
    ],
    ...restOfConfig
  };
  const apmInstance = apmInit(builtConfig);
  return new WebObservability(apmInstance, {
    transactionSampleRate: builtConfig.transactionSampleRate
  });
}
var WebObservability = class {
  constructor(apmInstance, config) {
    this.apmInstance = apmInstance;
    this.config = config;
    this.errorHandledMap = /* @__PURE__ */ new Map();
    if (typeof window !== "undefined") {
      window.addEventListener("error", (errorEvent) => {
        setTimeout(() => {
          if (errorEvent.error) {
            this.captureError(errorEvent.error, "Uncaught Error", {
              uncaught: true
            });
          }
        }, 2e3);
      });
      window.addEventListener("unhandledrejection", (promiseRejectionEvent) => {
        setTimeout(() => {
          if (promiseRejectionEvent.reason) {
            let error;
            if (promiseRejectionEvent.reason instanceof Error) {
              error = promiseRejectionEvent.reason;
            } else if (typeof promiseRejectionEvent.reason === "object") {
              error = new Error(JSON.stringify(promiseRejectionEvent.reason));
            } else {
              error = new Error(promiseRejectionEvent.reason);
            }
            this.captureError(error, "Unhandled Rejection", {
              uncaught: true
            });
          }
        }, 2e3);
      });
    }
    console.info("WebObservability: initialized");
  }
  /**
   * Captures and logs an error with additional metadata.
   *
   * @param {Error} error - The error object.
   * @param {string} errorLocation - The location of the error. Can be an error-boundary or anything that helps identifying the error.
   * @param {Object} [metadata] - Additional metadata related to the error.
   */
  captureError(error, errorLocation, metadata) {
    if (!errorLocation) {
      console.error("WebObservability: Error location is required");
      return;
    }
    const errorKey = error.message;
    if (metadata?.uncaught && this.errorHandledMap.get(errorKey)) {
      console.warn("WebObservability: Error already handled");
      return;
    }
    this.errorHandledMap.set(errorKey, true);
    if (Math.random() > this.config.transactionSampleRate) {
      console.warn("WebObservability: Error was not captured due to sample rate");
      return;
    }
    const customError = {
      ...metadata,
      message: `${error?.name}: ${error?.message}`,
      name: errorLocation,
      stack: error?.stack
    };
    this.apmInstance.captureError(customError);
    console.info(
      "WebObservability: Error captured",
      `${errorLocation} - ${error?.name}: ${error?.message}`
    );
  }
  /**
   * Sets the user context for APM monitoring.
   *
   * @param {Object} user - The user context object.
   * @param {string | number} [user.id] - The user ID.
   * @param {string} [user.username] - The username.
   * @param {string} [user.email] - The user email.
   */
  setUserContext(user) {
    this.apmInstance.setUserContext(user);
  }
  /**
   * Sets custom context information for APM monitoring.
   *
   * @param {Object} context - The custom context object.
   */
  setContext(context) {
    this.apmInstance.setCustomContext(context);
  }
};
export {
  configureWebObservability
};
//# sourceMappingURL=index.mjs.map